import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useFormStore } from '@/stores/formStore.ts'
import type { FormField } from '@/ts/types/schema.types'

export interface Mapping {
  [key: string]: Array<string>
}

export const useMappingStore = defineStore('mapping', () => {
  const FormStore = useFormStore()

  const _mappings = ref({})

  const mappings = computed(() => _mappings.value)

  const valuesOfParentByKey = computed(() => (parent: any, key: any) => mappings.value?.[parent as keyof object]?.[key])

  function initMappings(initialMappings: Mapping): void {
    _mappings.value = initialMappings
  }

  function getKeyByFormFieldId(serviceKey: string, formFieldId: string): string | undefined {
    const mapping: Mapping = mappings.value[serviceKey as keyof object] ?? {}
    return Object.keys(mapping).find((key: string) => mapping[key as keyof object].some((fieldId: string) => fieldId === formFieldId))
  }

  async function iterateFieldIdsOfKey(serviceKey: string, key: string, callback: Function): Promise<void> {
    const mappingAttribute: Array<string> = mappings.value[serviceKey as keyof object]?.[key]
    const callbacks: Array<Promise<boolean>> = mappingAttribute.map(async (fieldId: string) => await callback(fieldId)) ?? []
    await Promise.all(callbacks)
  }

  function getFieldIdsOfKey(serviceKey: string, key: string): Array<string> {
    const mappings = _mappings.value
    return mappings[serviceKey as keyof object]?.[key]
  }

  function getFieldsOfKey(serviceKey: string, key: string) {
    return computed<Array<FormField>>(() => {
      const keys = getFieldIdsOfKey(serviceKey, key)
      return keys.flatMap((key) => {
        if (!FormStore.fieldById(key).value) return []
        return [FormStore.fieldById(key).value as FormField]
      })
    })
  }

  async function iterateFieldIdsOfServiceKeys(
    firstServiceKey: string,
    secondServiceKey: string,
    key: string,
    callback: Function
  ): Promise<void> {
    await iterateFieldIdsOfKey(
      firstServiceKey,
      key,
      async (valueKey: string) => await iterateFieldIdsOfKey(secondServiceKey, valueKey, callback)
    )
  }

  return {
    initMappings,
    iterateFieldIdsOfKey,
    getKeyByFormFieldId,
    iterateFieldIdsOfServiceKeys,
    getFieldIdsOfKey,
    getFieldsOfKey,
    mappings,
    valuesOfParentByKey
  }
})
